import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { App } from 'antd';
import { createEditingJobNumberItem, jobNumbersAtomFamily, pendingJobNumberIdsAtomFamily } from '../atoms/settingAtoms';
import { addJobNumber, removeJobNumber } from '../api/callSettingMutations';
import { guardedClient } from '../../auth/api/clients';
import { extractProblemDetail } from '../../../shared/errors/problemDetail';
import { validateJobNumber } from '../utils/validateJobNumber';
import type { EditingJobNumberItem, JobNumberItem } from '../types';

interface JobNumbers {
  rows: JobNumberItem[];
  /** 追加・削除リクエスト送信中の行ID。該当行の入力欄と操作ボタンは無効化すること。 */
  pendingIds: ReadonlySet<string>;
  /** 編集中の行を追加（commitRow）できるか。追加ボタンの有効・無効に用いる。 */
  canCommitRow: (row: EditingJobNumberItem) => boolean;
  changeJobNumber: (id: string, value: string) => void;
  /** 編集中の行を検証してバックエンドへ登録し、成功したら committed に確定する。 */
  commitRow: (id: string) => Promise<void>;
  /** committed の行はバックエンドから削除した上で行を取り除く。編集中の行は行の取り消しのみ。 */
  removeRow: (id: string) => Promise<void>;
  appendRow: () => void;
}

/** 広告コード設定1件分の仕事Noリストの参照と編集操作を提供する。 */
export const useJobNumbers = (advertisementId: number): JobNumbers => {
  const { message } = App.useApp();
  const rows = useAtomValue(jobNumbersAtomFamily(advertisementId));
  const pendingIds = useAtomValue(pendingJobNumberIdsAtomFamily(advertisementId));
  const setRows = useSetAtom(jobNumbersAtomFamily(advertisementId));

  const changeJobNumber = useCallback(
    (id: string, value: string) => {
      // UI 側でも committed・送信中の行は入力欄を無効化しているが、フック側でも編集中の行に限定して契約を強制する
      setRows(prev =>
        prev.map(row => (row.id === id && row.status === 'editing' ? { ...row, jobNumber: value } : row))
      );
    },
    [setRows]
  );

  const appendRow = useCallback(() => {
    setRows(prev => [...prev, createEditingJobNumberItem()]);
  }, [setRows]);

  // commitRow 内の検証（validateJobNumber + 送信中ガード）と基準を一致させ、
  // ボタンの表示と実際の検証が食い違わないようにする。
  const canCommitRow = useCallback(
    (row: EditingJobNumberItem): boolean => {
      const trimmed = row.jobNumber.trim();
      return trimmed !== '' && !pendingIds.has(row.id) && validateJobNumber(rows, row.id, trimmed) === null;
    },
    [rows, pendingIds]
  );

  // useAtomCallback で安定参照のコールバックにし、rows 依存による全行再レンダリングを避ける。
  // コールバックはイベント時に命令的に1回だけ実行されるため、最新 rows を get で読める。
  // 副作用(message.error)は updater の外で呼ぶ（updater は純粋関数であるべきため）。
  const commitRow = useAtomCallback(
    useCallback(
      async (get, set, id: string) => {
        const pendingAtom = pendingJobNumberIdsAtomFamily(advertisementId);
        // 再レンダリング（ボタン無効化）前の連打による二重登録を防ぐ
        if (get(pendingAtom).has(id)) return;
        const current = get(jobNumbersAtomFamily(advertisementId));
        const target = current.find(row => row.id === id);
        if (target === undefined || target.status !== 'editing') return;
        const jobNumber = target.jobNumber.trim();
        if (jobNumber === '') return;
        const validationError = validateJobNumber(current, id, jobNumber);
        if (validationError !== null) {
          void message.error(validationError);
          return;
        }
        set(pendingAtom, prev => new Set(prev).add(id));
        // catch 内の return に暗黙に頼らず成功を明示し、エラー後に committed へ遷移する退行を防ぐ
        let succeeded = false;
        try {
          await addJobNumber(guardedClient, advertisementId, jobNumber);
          succeeded = true;
        } catch (error) {
          void message.error(extractProblemDetail(error) ?? `仕事No「${jobNumber}」の追加に失敗しました`);
        } finally {
          set(pendingAtom, prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
        if (!succeeded) return;
        set(jobNumbersAtomFamily(advertisementId), prev =>
          prev.map(row => (row.id === id ? { ...row, jobNumber, status: 'committed' } : row))
        );
      },
      [advertisementId, message]
    )
  );

  const removeRow = useAtomCallback(
    useCallback(
      async (get, set, id: string) => {
        const pendingAtom = pendingJobNumberIdsAtomFamily(advertisementId);
        // 送信中の再実行による同一仕事Noへの重複リクエストを防ぐ（commitRow と対称）
        if (get(pendingAtom).has(id)) return;
        const target = get(jobNumbersAtomFamily(advertisementId)).find(row => row.id === id);
        if (target === undefined) return;
        // 編集中の行はサーバー未登録のため、ローカルの行削除のみでよい。
        if (target.status === 'committed') {
          set(pendingAtom, prev => new Set(prev).add(id));
          // catch 内の return に暗黙に頼らず成功を明示し、エラー後に行が消える退行を防ぐ
          let succeeded = false;
          try {
            await removeJobNumber(guardedClient, advertisementId, target.jobNumber);
            succeeded = true;
          } catch (error) {
            void message.error(extractProblemDetail(error) ?? `仕事No「${target.jobNumber}」の削除に失敗しました`);
          } finally {
            set(pendingAtom, prev => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          }
          if (!succeeded) return;
        }
        set(jobNumbersAtomFamily(advertisementId), prev => prev.filter(row => row.id !== id));
      },
      [advertisementId, message]
    )
  );

  return { rows, pendingIds, canCommitRow, changeJobNumber, commitRow, removeRow, appendRow };
};
